// Secure AI Chat History Service - Uses API endpoints instead of direct Supabase calls
// This prevents exposing database credentials and operations to the frontend

import { AIChatHistory } from '../lib/supabase';

export class SecureAiChatHistoryService {
  private async getAuthToken(): Promise<string> {
    // Only use Supabase tokens for security - NO localStorage tokens
    try {
      const { supabase } = await import('../lib/supabase');
      if (supabase) {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (session?.access_token && !error) {
          return session.access_token;
        }
      }
    } catch (error) {
      console.log('Supabase not available');
    }

    throw new Error('Authentication required - please sign in with Supabase');
  }

  /**
   * Check if service is configured
   */
  isConfigured(): boolean {
    try {
      const { isSupabaseConfigured } = require('../lib/supabase');
      return isSupabaseConfigured();
    } catch (error) {
      return false;
    }
  }

  /**
   * Save an AI chat interaction using secure API endpoint
   */
  async saveChatInteraction(
    userId: string,
    messageContent: string,
    responseContent?: string,
    tokensUsed?: number
  ): Promise<boolean> {
    if (!this.isConfigured()) {
      console.log('Supabase not configured, skipping chat history save');
      return false;
    }

    try {
      const authToken = await this.getAuthToken();
      const response = await fetch('/api/ai/history', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          messageContent,
          responseContent,
          tokensUsed
        })
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.fallback) {
          // Fallback to localStorage (no chat history for localStorage users)
          console.log('Chat history requires Supabase authentication, skipping save');
          return false;
        }
        throw new Error(data.error || 'Failed to save chat history');
      }

      return true;
    } catch (error) {
      console.error('Error saving chat history via API:', error);
      return false;
    }
  }

  /**
   * Get chat history using secure API endpoint
   */
  async getChatHistory(
    userId: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<AIChatHistory[]> {
    if (!this.isConfigured()) {
      return [];
    }

    try {
      const authToken = await this.getAuthToken();
      const response = await fetch(`/api/ai/history?limit=${limit}&offset=${offset}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.fallback) {
          // Fallback to localStorage (no chat history for localStorage users)
          return [];
        }
        throw new Error(data.error || 'Failed to get chat history');
      }

      return data.chatHistory || [];
    } catch (error) {
      console.error('Error getting chat history via API:', error);
      return [];
    }
  }

  /**
   * Get total tokens used by a user (for analytics)
   */
  async getTotalTokensUsed(userId: string): Promise<number> {
    try {
      const chatHistory = await this.getChatHistory(userId, 1000); // Get more records for accurate count
      
      const totalTokens = chatHistory.reduce((sum, record) => {
        return sum + (record.tokens_used || 0);
      }, 0);

      return totalTokens;
    } catch (error) {
      console.error('Error calculating token usage:', error);
      return 0;
    }
  }

  /**
   * Get chat statistics for a user
   */
  async getChatStats(userId: string): Promise<{
    totalChats: number;
    totalTokens: number;
    averageTokensPerChat: number;
  }> {
    try {
      const chatHistory = await this.getChatHistory(userId, 1000); // Get more records for accurate stats
      
      const totalChats = chatHistory.length;
      const totalTokens = chatHistory.reduce((sum, record) => {
        return sum + (record.tokens_used || 0);
      }, 0);
      const averageTokensPerChat = totalChats > 0 ? totalTokens / totalChats : 0;

      return {
        totalChats,
        totalTokens,
        averageTokensPerChat: Math.round(averageTokensPerChat * 100) / 100
      };
    } catch (error) {
      console.error('Error calculating chat stats:', error);
      return { totalChats: 0, totalTokens: 0, averageTokensPerChat: 0 };
    }
  }

  /**
   * Delete old chat history using secure API endpoint
   */
  async deleteOldChatHistory(userId: string, daysOld: number = 30): Promise<boolean> {
    if (!this.isConfigured()) {
      return false;
    }

    try {
      const authToken = await this.getAuthToken();
      const response = await fetch(`/api/ai/history?daysOld=${daysOld}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.fallback) {
          // Fallback to localStorage (no chat history for localStorage users)
          return false;
        }
        throw new Error(data.error || 'Failed to delete chat history');
      }

      return true;
    } catch (error) {
      console.error('Error deleting chat history via API:', error);
      return false;
    }
  }
}

export const secureAiChatHistoryService = new SecureAiChatHistoryService(); 